import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BuilderBoard } from '@/components/build/BuilderBoard'
import { SetupWizard, type CompleteSetup } from '@/components/build/SetupWizard'
import { randomSeed } from '@/components/build/types'

// The whole recipe lives in the URL (instructor, workout, length, theme,
// format, adventure, seed), so a link IS a ride: the builder is deterministic
// per seed, and opening it rebuilds the same ride.
export function BuildPlaylistPage() {
  const [search, setSearch] = useSearchParams()
  const instructorId = search.get('i')
  const discipline = search.get('d')
  const minutes = Number(search.get('m'))
  const theme = search.get('t')
  const format = search.get('f')

  // Only read once: after this the board owns the seed and the dial.
  const [initial] = useState(() => {
    const adventure = Number(search.get('a'))
    return { seed: Number(search.get('s')) || randomSeed(), adventure: search.has('a') && adventure >= 0 && adventure <= 1 ? adventure : 0.5 }
  })

  const complete = Boolean(instructorId && discipline && minutes >= 5)
  const params = useMemo(
    () => ({ instructorId: instructorId ?? '', discipline: discipline ?? '', minutes, theme, format }),
    [instructorId, discipline, minutes, theme, format],
  )

  const build = (setup: CompleteSetup) => {
    const next = new URLSearchParams({ i: setup.instructorId, d: setup.discipline, m: String(setup.minutes), a: '0.5', s: String(randomSeed()) })
    if (setup.theme) next.set('t', setup.theme)
    if (setup.format) next.set('f', setup.format)
    setSearch(next)
  }

  // Keep the address bar current without a navigation: this runs on every
  // nudge of the dial, and must not re-render the router or clobber its
  // history bookkeeping (hence passing history.state through).
  const syncRecipe = useCallback(({ seed, adventure }: { seed: number; adventure: number }) => {
    const url = new URL(window.location.href)
    url.searchParams.set('s', String(seed))
    url.searchParams.set('a', adventure.toFixed(2))
    window.history.replaceState(window.history.state, '', url)
  }, [])

  if (!complete) return <SetupWizard onBuild={build} />

  return <BuilderBoard key={`${instructorId}|${discipline}|${minutes}|${theme}|${format}`} params={params} initial={initial} onRecipeChange={syncRecipe} />
}
